
import React, { useState, useEffect } from 'react';
import { getVersionInfo, forceAppRefresh } from '@/utils/app-version';
import { Button } from './button';
import { cn } from '@/lib/utils';
import { RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { isStandalone } from '@/utils/device';

interface VersionDisplayProps {
  className?: string;
  showRefreshButton?: boolean;
}

export function VersionDisplay({ className, showRefreshButton = true }: VersionDisplayProps) {
  // Initialize with safe default values
  const [versionInfo, setVersionInfo] = useState(() => getVersionInfo());
  const [isPwa, setIsPwa] = useState(false);
  const [hasCheckedForUpdates, setHasCheckedForUpdates] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    // Check if running as PWA
    setIsPwa(isStandalone());
    
    const checkVersion = () => {
      try {
        // Get version info and ensure it's properly formatted
        const info = getVersionInfo();
        setVersionInfo(info);
        setHasCheckedForUpdates(true);
      } catch (error) {
        console.error('[Version Display] Error checking version:', error);
      }
    };
    
    // Check immediately then every minute
    checkVersion();
    const interval = setInterval(checkVersion, 60000);
    
    return () => clearInterval(interval);
  }, []);

  // Format date for display
  let formattedDate = 'unknown';
  if (versionInfo.buildTimestamp && versionInfo.buildTimestamp !== 'unknown') {
    try {
      const date = new Date(versionInfo.buildTimestamp);
      // Check if date is valid
      if (!isNaN(date.getTime())) {
        formattedDate = date.toLocaleDateString();
      }
    } catch {
      formattedDate = 'unknown';
    }
  }

  const handleRefresh = () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    
    toast.info('Refreshing application', {
      description: 'Clearing cache and reloading...'
    });
    
    // Short delay to allow toast to show
    setTimeout(() => {
      try {
        forceAppRefresh();
      } catch (error) {
        console.error('[Version Display] Error during refresh:', error);
        toast.error('Refresh failed', {
          description: 'Please try again or restart the application manually'
        });
        setIsRefreshing(false);
      }
    }, 800);
  };

  return (
    <div className={cn("flex items-center text-xs text-muted-foreground gap-2", className)}>
      <span>
        v{versionInfo.version} ({formattedDate})
        {isPwa && " [PWA]"}
      </span>
      {showRefreshButton && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={handleRefresh}
          title="Force refresh app"
          disabled={isRefreshing}
        >
          <RefreshCcw className={cn(
            "h-3 w-3", 
            hasCheckedForUpdates && "text-green-500",
            isRefreshing && "animate-spin"
          )} />
        </Button>
      )}
    </div>
  );
}
